import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { scopeToSite } from '../../lib/siteScope';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Avatar from '../../components/ui/Avatar';
import StatusPill from '../../components/ui/StatusPill';
import PageHeader from '../../components/ui/PageHeader';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import { IconDownload } from '@tabler/icons-react';

export default function QualificationReport() {
  const { orgId, userProfile, role } = useAuth();
  const siteId = userProfile?.siteId;
  const navigate = useNavigate();
  const [staffWithQuals, setStaffWithQuals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const staffSnap = await getDocs(collection(db, 'organisations', orgId, 'users'));
        const staff = scopeToSite(staffSnap.docs.map(d => ({ id: d.id, ...d.data() })), role, siteId);
        const enriched = await Promise.all(staff.map(async (member) => {
          const qualsSnap = await getDocs(collection(db, 'organisations', orgId, 'users', member.id, 'qualifications'));
          const quals = qualsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          const today = new Date();
          const enrichedQuals = quals.map(q => {
            if (!q.expiryDate) return { ...q, status: 'valid' };
            const expiry = new Date(q.expiryDate);
            const days = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
            const status = days < 0 ? 'expired' : days <= 30 ? 'expiring' : 'valid';
            return { ...q, status, daysUntilExpiry: days };
          });
          return { ...member, qualifications: enrichedQuals };
        }));
        setStaffWithQuals(enriched.filter(m => m.role === 'operator' || m.role === 'manager'));
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const totalQuals = staffWithQuals.reduce((sum, m) => sum + m.qualifications.length, 0);
  const expiredCount = staffWithQuals.reduce((sum, m) => sum + m.qualifications.filter(q => q.status === 'expired').length, 0);
  const expiringCount = staffWithQuals.reduce((sum, m) => sum + m.qualifications.filter(q => q.status === 'expiring').length, 0);

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <PageHeader
          title="Qualification status report"
          subtitle="All staff qualifications and expiry dates"
          actions={<Button variant="secondary" icon={<IconDownload size={14} stroke={1.5} />} onClick={() => toast.info('PDF export coming soon')}>Export PDF</Button>}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Staff members', value: staffWithQuals.length, colour: 'var(--color-text-primary)' },
            { label: 'Total qualifications', value: totalQuals, colour: 'var(--color-text-primary)' },
            { label: 'Expiring soon', value: expiringCount, colour: expiringCount > 0 ? 'var(--color-warning)' : 'var(--color-success)' },
            { label: 'Expired', value: expiredCount, colour: expiredCount > 0 ? 'var(--color-danger)' : 'var(--color-success)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
              <div style={{ fontSize: 28, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>
        {loading ? <LoadingSkeleton rows={5} card /> : staffWithQuals.map(member => (
          <Card key={member.id} style={{ marginBottom: 10 }} onClick={() => navigate('/staff/' + member.id)} hover>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: member.qualifications.length ? 12 : 0 }}>
              <Avatar name={member.name} photoURL={member.photoURL} size="md" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, color: 'var(--color-text-primary)' }}>{member.name}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', textTransform: 'capitalize' }}>{member.role} · {member.siteName || '—'}</div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{member.qualifications.length} qualification{member.qualifications.length !== 1 ? 's' : ''}</div>
            </div>
            {member.qualifications.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {member.qualifications.map(qual => (
                  <div key={qual.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                    <div style={{ fontSize: 12, color: qual.status === 'expired' ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>{qual.type}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {qual.expiryDate && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{qual.expiryDate}</div>}
                      <StatusPill status={qual.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
    </Layout>
  );
}